﻿import { Avatar3dApi, Avatar3dEdits, ShowEditorOptions } from './Avatar3dApi.ts'
import { RundotGameAPI } from '../rundot-game-api/types.ts'
import { Avatar3dConfig } from './Avatar3dConfig.ts'
import { AssetManifest, Category } from './AssetManifest.ts'
import { createMockDelay, MOCK_DELAYS } from '../rundot-game-api/systems/core.js'

declare global {
  interface Window {
    RundotGamePrototyping?: {
      showAvatarEditorOverlay: (
        options: any,
        resolve: (args: any) => void,
      ) => Promise<void>
    }
  }
}

export class MockAvatarApi implements Avatar3dApi {
  private readonly _rundotGameApi: RundotGameAPI

  private cachedAssets: Record<string, Category> | null = null
  private cachedVersion: string = ''

  constructor(rundotGameApi: RundotGameAPI) {
    this._rundotGameApi = rundotGameApi
  }
  downloadAssetPaths(): Promise<Record<string, string[]>> {
    return this.getAllAssetPaths()
  }

  async deleteAvatar(): Promise<void> {
    const rundotGameApi = this._rundotGameApi

    // Get current profile from RUN.world API context
    const currentProfile = rundotGameApi.getProfile()
    const profileId = currentProfile?.id || 'default_profile'

    // Remove from localStorage
    localStorage.removeItem(`rundot-game-mock-avatar3d-${profileId}`)
  }

  async loadAvatar(avatar3dId?: string): Promise<Avatar3dConfig | null> {
    const rundotGameApi = this._rundotGameApi
    let config

    if (avatar3dId) {
      // Mock shared avatar3d loading using deterministic selection
      config = await this.selectAvatarConfig(avatar3dId, false)
    } else {
      // Always return a valid avatar, even if no profile
      const currentProfile = rundotGameApi.getProfile()
      const profileId = currentProfile?.id || 'default_profile'

      // Always generate fresh avatar config with current CDN URLs
      // localStorage is only used for save persistence, not loading
      if (profileId === 'default_profile') {
        // Use consistent defaults for the default profile
        config = await this.selectAvatarConfig(null, true)
      } else {
        // Use seeded random for other profiles
        config = await this.selectAvatarConfig(profileId, false)
      }
    }

    // Return direct config or null if not found
    return config || null
  }

  async saveAvatar(config: Avatar3dConfig): Promise<string> {
    const rundotGameApi = this._rundotGameApi

    // Get current profile from RUN.world API context
    const currentProfile = rundotGameApi.getProfile()
    const profileId = currentProfile?.id || 'default_profile'

    // Save to localStorage for mock persistence
    localStorage.setItem(
      `rundot-game-mock-avatar3d-${profileId}`,
      JSON.stringify(config),
    )

    // Generate a mock avatar3dId
    const mockAvatar3dId = `mock_avatar3d_${profileId}_${Date.now()}`

    // Return direct avatar ID
    return mockAvatar3dId
  }

  downloadManifest(): Promise<AssetManifest> {
    return this.loadAssetsManifest()
  }

  async showEditor(options?: ShowEditorOptions): Promise<Avatar3dEdits> {
    // Mock implementation - simulate opening avatar editor
    if (window.RundotGamePrototyping) {
      // If prototyping utilities are available, use them for a better experience
      return new Promise((resolve) => {
        window.RundotGamePrototyping!.showAvatarEditorOverlay(options, resolve)
      })
    }

    // Simple browser-based simulation
    // Simulate delay for opening editor
    await createMockDelay(MOCK_DELAYS.medium)

    // Generate a mock avatar configuration
    const mockAvatarConfig = await this.selectAvatarConfig(null, false)

    // Simulate user interaction - 70% chance they save changes, 30% they cancel
    const userSavedChanges = Math.random() > 0.3

    // Return direct editor result
    if (userSavedChanges) {
      const mockAvatarId = `mock_avatar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      return {
        wasChanged: true,
        config: mockAvatarConfig,
        savedAvatarId: mockAvatarId,
      }
    } else {
      return {
        wasChanged: false,
        config: null,
        savedAvatarId: null,
      }
    }
  }

  private async getAssets() {
    let cachedAssets = this.cachedAssets
    if (!cachedAssets) {
      cachedAssets = (await this.loadAssetsManifest()).categories
    }
    return cachedAssets
  }

  private async loadAssetsManifest() {
    const rundotGameApi = this._rundotGameApi

    // Use the CDN base URL from RUN.world API configuration
    const baseManifestUrl = rundotGameApi.cdn.resolveAvatarAssetUrl('assets.json')

    // Add cache busting to prevent CDN/browser cache issues
    const separator = baseManifestUrl.includes('?') ? '&' : '?'
    const manifestUrl = `${baseManifestUrl}${separator}v=${Date.now()}`

    try {
      const response = await fetch(manifestUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        cache: 'no-cache',
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch assets manifest: ${response.status} ${response.statusText}`,
        )
      }

      const manifest = (await response.json()) as AssetManifest

      // Cache the assets but not the raw JSON
      this.cachedAssets = manifest.categories || {}
      this.cachedVersion = manifest.version

      return manifest
    } catch (error) {
      console.error('[RUN:mock] Failed to load assets manifest:', error)

      // Return a fallback minimal manifest for development
      console.warn(
        '[RUN:mock] Using fallback minimal manifest for development',
      )
      const fallbackManifest: AssetManifest = {
        version: 'dev-fallback',
        generatedAt: new Date().toISOString(),
        categories: {
          head: {
            type: 'mesh',
            assets: [
              {
                id: 'head_default',
                filename: 'head_default.glb',
                displayName: 'Default Head',
              },
              {
                id: 'head_variant1',
                filename: 'head_variant1.glb',
                displayName: 'Head Variant 1',
              },
            ],
          },
          outfit: {
            type: 'mesh',
            assets: [
              {
                id: 'outfit_default',
                filename: 'outfit_default.glb',
                displayName: 'Default Outfit',
              },
              {
                id: 'outfit_casual',
                filename: 'outfit_casual.glb',
                displayName: 'Casual Outfit',
              },
            ],
          },
          hat: {
            type: 'mesh',
            assets: [
              {
                id: 'hat_none',
                filename: 'hat_none.glb',
                displayName: 'No Hat',
              },
              {
                id: 'hat_cap',
                filename: 'hat_cap.glb',
                displayName: 'Baseball Cap',
              },
            ],
          },
          hair: {
            type: 'mesh',
            assets: [
              {
                id: 'hair_default',
                filename: 'hair_default.glb',
                displayName: 'Default Hair',
              },
              {
                id: 'hair_long',
                filename: 'hair_long.glb',
                displayName: 'Long Hair',
              },
            ],
          },
          'face-accessory': {
            type: 'mesh',
            assets: [
              {
                id: 'glasses_none',
                filename: 'glasses_none.glb',
                displayName: 'No Glasses',
              },
              {
                id: 'glasses_regular',
                filename: 'glasses_regular.glb',
                displayName: 'Regular Glasses',
              },
            ],
          },
          animation: {
            type: 'mesh',
            assets: [
              {
                id: 'idle_default',
                filename: 'idle_default.glb',
                displayName: 'Default Idle',
              },
              {
                id: 'wave',
                filename: 'wave.glb',
                displayName: 'Wave Animation',
              },
            ],
          },
        },
      }

      this.cachedAssets = fallbackManifest.categories
      this.cachedVersion = fallbackManifest.version

      return fallbackManifest
    }
  }

  private async selectAvatarConfig(
    avatarId: string | null = null,
    useDefaults = false,
  ): Promise<Avatar3dConfig> {
    const categories = await this.getAssets()

    const rng = avatarId ? this.seededRandom(avatarId) : () => Math.random()

    const headAsset = this.selectAsset(
      categories,
      'head',
      rng,
      avatarId,
      useDefaults,
    )

    const outfitAsset = this.selectAsset(
      categories,
      'outfit',
      rng,
      avatarId,
      useDefaults,
    )
    const hatAsset = this.selectAsset(
      categories,
      'hat',
      rng,
      avatarId,
      useDefaults,
    )
    const hairAsset = this.selectAsset(
      categories,
      'hair',
      rng,
      avatarId,
      useDefaults,
    )
    const faceAccessoryAsset = this.selectAsset(
      categories,
      'face-accessory',
      rng,
      avatarId,
      useDefaults,
    )
    const animationAsset = this.selectAsset(
      categories,
      'animation',
      rng,
      avatarId,
      useDefaults,
    )

    const config: Avatar3dConfig = {
      headAsset: headAsset.filename,
      outfitAsset: outfitAsset.filename,
      animationAsset: animationAsset.filename,
      faceAccessoryAsset: faceAccessoryAsset.filename,
      hairAsset: hairAsset.filename,
      hatAsset: hatAsset.filename,
      skinColor: null, // NOTE(Zee): This was not selected anywhere?
    }

    return config
  }

  private selectAsset(
    manifest: Record<string, Category>,
    categoryId: string,
    rng: () => number,
    avatarId: string | null,
    useDefaults = false,
  ) {
    const categoryAssets = manifest[categoryId].assets

    if (useDefaults) {
      // Always use the first asset for default avatar (consistent)
      const selectedAsset = categoryAssets[0]
      return selectedAsset
    }

    if (avatarId) {
      // Use seeded random for deterministic selection based on ID
      const index = Math.floor(rng() * categoryAssets.length)
      const selectedAsset = categoryAssets[index]
      return selectedAsset
    }

    // Fallback to first asset if no ID
    return categoryAssets[0]
  }

  private seededRandom(seed: string) {
    const hash = this.simpleHash(seed)
    let state = hash
    return function () {
      state = (state * 1664525 + 1013904223) % 4294967296
      return state / 4294967296
    }
  }

  private simpleHash(str: string) {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash)
  }

  private async getAllAssetPaths() {
    const rundotGameApi = this._rundotGameApi
    const assets = await this.getAssets()
    const allPaths: Record<string, string[]> = {}

    for (const [category, categoryData] of Object.entries(assets)) {
      allPaths[category] = (categoryData?.assets || []).map((asset) =>
        rundotGameApi.cdn.resolveAvatarAssetUrl(`${category}/${asset.filename}`),
      )
    }

    return allPaths
  }
}
